/**
 * Processing forms that are not a cob-chain stage.
 *
 * SLOC Updation (seedtrac's bin-to-bin relocation inside one plant, rebuilt from the SLOCUpdation
 * Android app) and the four forms of seedtrac's Processing login (manual p316-325,
 * `docs/PROCESSING_BACKEND.md`): Drying Slip, Processing Slip, Processing and Packing Slip, and Lot
 * Blending. Those four have their own pages and API module (`/processing`); their configs exist for
 * the registry -- per-form grants, titles, number prefixes.
 */
import { fullTransport } from "./arrival";
import type { FormConfig } from "./types";

/**
 * SLOC Updation: lots moved from one SLOC to another at the same plant, in two legs.
 *
 * - PICK: the operator takes lots off one or more SLOCs onto their pending list (a draft), then
 *   SUBMITs it. That numbers it `SLOC/0001/Y/D` and opens it as a transaction (status `previewed`).
 *   Picked stock stays reserved at its source bin until it is placed.
 * - PLACE: anyone opens the transaction, chooses a destination sub-bin and places some or all of what
 *   is left; each placement posts a `relocation` ledger pair. SUBMIT closes it (status `submitted`)
 *   once nothing is left to place.
 *
 * The module has its own handheld screens and API (`/sloc-updation`); this config exists for the
 * registry — per-form grants, the title and the number prefix.
 */
export const slocUpdation: FormConfig = {
  code: "sloc_updation",
  docType: "processing",
  title: "SLOC Updation",
  numberPrefix: "SLOC",
  numberPad: 4,
  sourceKind: "storage",
  destKind: "storage",
  declaredLabel: null,
  declaredFeed: false,
  lotSource: "manual",
  allowManualLots: false,
  blindCoded: false,
  multipleMaterials: true,
  qaRequired: false,
  receivedCaptured: false,
  bagWise: false,
  enforceUniqueLotNumber: false,
  transport: { ...fullTransport, enabled: false },
  dcInfo: false,
  raisedOnHandheld: true,
  headerFields: [],
  lineColumns: [
    "lot_number",
    "crop",
    "variety",
    "stage",
    "source_sloc",
    "actions",
  ],
  documents: [],
};

export const DRYING_SLIP_FORM = "processing_drying";
export const PROCESSING_SLIP_FORM = "processing_slip";
export const PROCESSING_PACKING_SLIP_FORM = "processing_packing_slip";
export const LOT_BLENDING_FORM = "processing_blending";
/**
 * WB Packing Slip, the packing work order (docs/designs/packaging-module.md P2). It rides the
 * Processing slip engine (numbering, versions, the lot gate and holds) under doc_type `packaging`:
 * Release is the engine's submit, and moves no stock -- it holds the lots until the Packing Slip packs.
 */
export const PACKING_WORK_ORDER_FORM = "packing_work_order";
/**
 * Re-Printing Packing Slip (legacy seedtrac "Re-Printing Packing Slip", user 2026-10-28): the WB
 * Packing Slip's twin for packed (P) lots already in PSW bins. The floor re-labels them on the handheld
 * (new WB / MP stickers and labels); the lot keeps its number and no stock moves.
 */
export const REPRINT_WORK_ORDER_FORM = "reprint_work_order";
/**
 * The crop category whose crops are root stock (docs/designs/root-stock-packing.md): packed in
 * packets of a fixed seed count. A WB Packing Slip of a root stock crop plans each lot's Nos too, and
 * its Packing Slip counts them (TRP series).
 */
export const ROOT_STOCK_CATEGORY_CODE = "RT";
/**
 * Pack Seed Unpacking – Pack to Condition, P2C (legacy seedtrac `TPC/PC…`, user 2026-10-28): pouches
 * of a packed (P) lot taken out of their PSW bins, Entire or per bin, back to condition seed. The seed
 * becomes the lot's next Condition batch ("Batch No. Generated – YES") in the bins the slip names.
 */
export const PACK_P2C_FORM = "pack_p2c";
/**
 * Inter Variety Transfer, IVT (legacy seedtrac `TVT/VT…` with its IVTN, user 2026-10-28): Condition
 * lots moved from one variety to another of the same crop. Each lot, whole or per bin, becomes its
 * next batch under the To variety in the IVT Lot SLOC; the Periodical IVT report reads it.
 */
export const IVT_FORM = "ivt";
/**
 * OFL Packing Slip (legacy seedtrac "OFL Packing slip", user 2026-10-28): a field-crop lot, Raw or
 * Condition, packed whole into kg pouches in cartons. Each carton is keyed with its barcode and its
 * net and gross weight; the packed lot (same batch, stage P) goes to the pack seed bins.
 */
export const OFL_PACKING_FORM = "ofl_packing_slip";
/**
 * Pack Seed Re-Printing (legacy seedtrac `RV…` after its `TRV…` QC Sample Request, user 2026-10-28):
 * a packed lot re-tested by QC is re-labelled with a new validity. Its pouches, Entire or per PSW bin,
 * less a QC sample and the re-printing loss, become the lot's next P batch in the pack seed bins.
 */
export const PACK_REVALIDATE_FORM = "pack_revalidate";
/**
 * Packing Slip (Field Crop) (legacy seedtrac `add_packfc_slip.php`, user 2026-10-28): a field-crop lot,
 * Raw or Condition, Entire or per bin, packed into one kg UPS; Packing Loss and Captive Consumption
 * come off and the packed lot (stage P: same batch whole, next batch part) goes to the PSW bins.
 */
export const FC_PACKING_FORM = "fc_packing_slip";
/**
 * Thermal (`TH/…`, docs/designs/thermal-treatment.md): one lot's oven heat treatment, sent from PP
 * Appearance and done in batches the oven can hold. The lot keeps its number; Submit posts the loss.
 */
export const THERMAL_FORM = "processing_thermal";
/**
 * Priming (`PR/…`, docs/designs/priming.md): a lot QC recommended, issued out of its bins (whole, or
 * part as its next batch), primed in batches and returned. The returned lot is Primed for good.
 */
export const PRIMING_FORM = "processing_priming";
/**
 * Color Sorting Slip (`TCS/…`, docs/designs/color-sorting.md): whole lots through the colour sorter,
 * recorded once however many passes it took. The good seed stays under the lot's number; the rest is
 * booked as Colour Sorting Reject (CSR).
 */
export const COLOR_SORT_FORM = "processing_color_sort";
/**
 * Treatment Slip (`TTR/…`, QC review 2026-11-03): Condition lots treated after processing, often days
 * or weeks later. The lot keeps its number and its bins and no stock moves; the slip is the record of
 * the Treatment Schema it took, and Final Submit sends a vegetable lot back to QC (UT) for PP,
 * moisture and germination.
 */
export const TREATMENT_FORM = "processing_treatment";
export const PROCESSING_FORMS = [
  DRYING_SLIP_FORM,
  PROCESSING_SLIP_FORM,
  PROCESSING_PACKING_SLIP_FORM,
  LOT_BLENDING_FORM,
  PACKING_WORK_ORDER_FORM,
  REPRINT_WORK_ORDER_FORM,
  THERMAL_FORM,
  PRIMING_FORM,
  COLOR_SORT_FORM,
  TREATMENT_FORM,
  PACK_P2C_FORM,
  IVT_FORM,
  OFL_PACKING_FORM,
  PACK_REVALIDATE_FORM,
  FC_PACKING_FORM,
] as const;
/** The packing floor's work orders: planned on the desk, released to the floor, completed by it. */
export const WORK_ORDER_FORMS: readonly ProcessingFormCode[] = [
  PACKING_WORK_ORDER_FORM,
  REPRINT_WORK_ORDER_FORM,
];
export type ProcessingFormCode = (typeof PROCESSING_FORMS)[number];

const processingForm = (
  code: ProcessingFormCode,
  title: string,
  numberPrefix: string
): FormConfig => ({
  ...slocUpdation,
  code,
  title,
  numberPrefix,
  raisedOnHandheld: false,
  lineColumns: ["lot_number", "crop", "variety", "stage", "actions"],
});

/** Drying Slip (`TPD/0001/Y/D`): lots dried in their bin, whole or part; the stage stays. */
export const dryingSlip = processingForm(
  DRYING_SLIP_FORM,
  "Drying Slip",
  "TPD"
);
/** Processing Slip (`TPS/…`): picked seed through the line, condition seed into the Updated SLOC. */
export const processingSlip = processingForm(
  PROCESSING_SLIP_FORM,
  "Processing Slip",
  "TPS"
);
/** Processing and Packing Slip (`TPK/…`): the Processing Slip, then the packed part into pouches. */
export const processingPackingSlip = processingForm(
  PROCESSING_PACKING_SLIP_FORM,
  "Processing and Packing Slip",
  "TPK"
);
/**
 * Lot Blending (`TLB/…`): a REQUEST Processing raises (Preview), the QC Supervisor authorises or
 * rejects, and the blend itself (a 9-series lot placed in SLOC) happens after authorisation.
 */
export const lotBlending = processingForm(
  LOT_BLENDING_FORM,
  "Lot Blending",
  "TLB"
);
/** Thermal (`TH/…`): batches of one lot through the oven; the lot and its stage stay. */
export const thermalTreatment = processingForm(THERMAL_FORM, "Thermal", "TH");
/** Priming (`PR/…`): Issue is the submit; batches and Return follow while the seed is out. */
export const primingTreatment = processingForm(PRIMING_FORM, "Priming", "PR");
/** Color Sorting Slip (`TCS/…`): the Drying Slip's sibling; good seed back, rejects booked as CSR. */
export const colorSortSlip = processingForm(
  COLOR_SORT_FORM,
  "Color Sorting Slip",
  "TCS"
);
/** Treatment Slip (`TTR/…`): whole Condition lots treated in their bins; nothing moves. */
export const treatmentSlip = processingForm(
  TREATMENT_FORM,
  "Treatment Slip",
  "TTR"
);
/**
 * What each processing step is called on a lot's history (Lot Biography, user 2026-10-28: "it is so
 * confusing whether we made batches or did thermal or color sorting"), and what a lot that went
 * through it is ("Thermal treated"). CSW Batch Creation is the warehouse's `csw_batch`.
 */
export const PROCESS_STEP_LABELS: Readonly<Record<string, string>> = {
  processing_drying: "Drying",
  processing_thermal: "Thermal",
  processing_priming: "Priming",
  processing_color_sort: "Color Sorting",
  processing_treatment: "Treatment",
  processing_slip: "Processing",
  processing_packing_slip: "Processing & Packing",
  processing_blending: "Lot Blending",
  packing_work_order: "WB Packing Slip",
  reprint_work_order: "Re-Printing",
  pack_p2c: "Unpacking (P2C)",
  ivt: "Inter Variety Transfer",
  ofl_packing_slip: "OFL Packing",
  pack_revalidate: "Pack Seed Re-Printing",
  fc_packing_slip: "Packing (Field Crop)",
  packing_slip: "Packing",
  csw_batch: "CSW Batch",
};
export const PROCESS_STEP_DONE: Readonly<Record<string, string>> = {
  processing_drying: "Dried",
  processing_thermal: "Thermal treated",
  processing_priming: "Primed",
  processing_color_sort: "Color sorted",
  processing_treatment: "Treated",
  processing_slip: "Processed",
  processing_packing_slip: "Processed & packed",
  processing_blending: "Blended",
  packing_slip: "Packed",
  reprint_work_order: "Re-printed",
  pack_p2c: "Unpacked",
  ivt: "Variety transferred",
  ofl_packing_slip: "Packed (OFL)",
  pack_revalidate: "Re-printed (new validity)",
  fc_packing_slip: "Packed (Field Crop)",
  csw_batch: "Batched",
};

/** WB Packing Slip (`TPW/…`): Condition lots planned for packing, released to the packing floor. */
export const packingWorkOrder: FormConfig = {
  ...processingForm(PACKING_WORK_ORDER_FORM, "WB Packing Slip", "TPW"),
  docType: "packaging",
};
/** Packing Slip (Field Crop) (`TFC/…`): a field-crop lot packed into one kg UPS, loss and CC off. */
export const fcPackingSlip: FormConfig = {
  ...processingForm(FC_PACKING_FORM, "Packing Slip (Field Crop)", "TFC"),
  docType: "packaging",
};
/** Pack Seed Re-Printing (`TRV/…`): a re-tested packed lot re-labelled as its next batch. */
export const packRevalidate: FormConfig = {
  ...processingForm(PACK_REVALIDATE_FORM, "Pack Seed Re-Printing", "TRV"),
  docType: "packaging",
};
/** OFL Packing Slip (`TOF/…`): a field-crop lot packed whole into kg pouches in weighed cartons. */
export const oflPackingSlip: FormConfig = {
  ...processingForm(OFL_PACKING_FORM, "OFL Packing Slip", "TOF"),
  docType: "packaging",
};
/** Inter Variety Transfer (`TVT/…`): Condition lots to another variety, each its next batch. */
export const ivt: FormConfig = {
  ...processingForm(IVT_FORM, "Inter Variety Transfer", "TVT"),
  docType: "packaging",
};
/** Pack Seed Unpacking – P2C (`TPC/…`): packed pouches back to the lot's next Condition batch. */
export const packP2c: FormConfig = {
  ...processingForm(PACK_P2C_FORM, "Pack Seed Unpacking (P2C)", "TPC"),
  docType: "packaging",
};
/** Re-Printing Packing Slip (`TRW/…`): packed lots released to the floor for new labels. */
export const reprintWorkOrder: FormConfig = {
  ...processingForm(REPRINT_WORK_ORDER_FORM, "Re-Printing Packing Slip", "TRW"),
  docType: "packaging",
};

/**
 * Packing Slip (`TPP/…`, docs/designs/packaging-module.md P3): a released WB Packing Slip packed into
 * pouches. Its own module (`/packaging/packing-slips`), not a Processing-engine form: Final Submit
 * mints the packed lot (C → P), moves the stock and completes the work order.
 */
export const PACKING_SLIP_FORM = "packing_slip";
/**
 * Unpackaging (legacy seedtrac `add_unpacking.php`, user 2026-10-28): master packs opened back into
 * pouches or window boxes. A register of retired MP barcodes (handheld floor MPs and OFL cartons):
 * the pouches stay in their PSW bin, so no stock moves (`/packaging/unpackings`).
 */
export const UNPACKING_FORM = "pack_unpacking";
/**
 * Root Stock Nos Count (`TRC/…`, docs/designs/root-stock-packing.md): packed root stock that has no
 * seed count yet (packed before Nos were kept, or opening stock) counted once, bin by bin, in one
 * count pack size. Submit makes the lot counted, so it can move again; no kg moves.
 */
export const ROOT_STOCK_NOS_COUNT_FORM = "root_stock_nos_count";
export const rootStockNosCount: FormConfig = {
  ...slocUpdation,
  code: ROOT_STOCK_NOS_COUNT_FORM,
  docType: "packaging",
  title: "Root Stock Nos Count",
  numberPrefix: "TRC",
  raisedOnHandheld: false,
  lineColumns: ["lot_number", "crop", "variety", "stage", "actions"],
};
export const packUnpacking: FormConfig = {
  ...slocUpdation,
  code: UNPACKING_FORM,
  docType: "packaging",
  title: "Unpackaging",
  numberPrefix: "TUP",
  raisedOnHandheld: false,
  lineColumns: ["lot_number", "crop", "variety", "stage", "actions"],
};
export const packingSlip: FormConfig = {
  ...slocUpdation,
  code: PACKING_SLIP_FORM,
  docType: "packaging",
  title: "Packing Slip",
  numberPrefix: "TPP",
  raisedOnHandheld: false,
  lineColumns: ["lot_number", "crop", "variety", "stage", "actions"],
};
